import axios from "axios";
import { API } from "../../backend";
import { useAppContext } from "../../hooks/context/AppContext";
import { errorToast } from "../../Utils/toast";

/**
 * useApiClientBase is a custom hook that creates an Axios API client instance
 * with authentication headers and provides a standardized error handler.
 * It uses the current authentication data from context.
 *
 * @returns { apiClient, handleError } - The configured Axios client and error handler function.
 */
export const useApiClientBase = () => {
  const { authData } = useAppContext();

  /**
   * Create an Axios client instance with the base API URL and auth headers.
   * The Authorization header uses the current user's ID token.
   */
  const apiClient = axios.create({
    baseURL: API,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authData.idToken}`,
    },
  });

  /**
   * Handles API errors, including token expiration and forbidden errors.
   * Optionally handles 403 errors and shows a toast for other errors.
   *
   * @param error - The error object from Axios or the API.
   * @param context - A string describing the context of the error (for logging/toast).
   * @returns null (always returns null for error cases)
   */
  const handleError = (error: any, context: string) => {
    if (error?.response?.status === 400) {
      errorToast("Bad Request: " + (error.response.data?.message || context));
      return null;
    }

    if (error?.response?.status === 401) {
      errorToast("Unauthorized: Please log in again.");
      // Optionally, trigger a logout or redirect to login
      return null;
    }

    if (error?.response?.status === 403) {
      errorToast(
        "Forbidden: You do not have permission to perform this action."
      );
      // Optionally, handle forbidden access (e.g., redirect, silent fail, etc.)
      return null;
    }

    if (error?.response?.status === 404) {
      errorToast("Not Found: The requested resource could not be found.");
      return null;
    }

    if (error?.response?.status === 500) {
      errorToast("Server Error: Please try again later.");
      return null;
    }

    // Handle any other error?.response?.status codes
    if (error?.response?.status) {
      errorToast(
        `Error ${error?.response?.status}: ${
          error.response.data?.message || context
        }`
      );
      return null;
    }
    errorToast(error.title || context);
    return null;
  };

  return { apiClient, handleError };
};
